/**
 * Microsoft Clarity custom tags only. Clarity is loaded via Google Tag Manager.
 * This service just calls clarity("set", key, value) when available for buying journey tags.
 */

import { GTM_ALLOWED_HOSTNAMES } from '@/config/gtm'
import { isYouthEnvironment } from '@/config/youthEnvironment'
import { ALLOW_LOCALHOST_FOR_JOURNEY_TESTING } from '@/constants/buyingJourney'

const MAX_TAG_LENGTH = 255

function isProductionDomain() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false
  return GTM_ALLOWED_HOSTNAMES.includes(window.location.hostname)
}

function isLocalhost() {
  if (typeof window === 'undefined' || !window.location?.hostname) return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

function isClarityTagAllowed() {
  if (isYouthEnvironment()) return false
  return (
    isProductionDomain() ||
    (ALLOW_LOCALHOST_FOR_JOURNEY_TESTING && isLocalhost())
  )
}

function isClarityLoaded() {
  return typeof window !== 'undefined' && typeof window.clarity === 'function'
}

let clarityTagLogged = false

/**
 * Set a Clarity custom tag (for filtering sessions). No-op when not allowed or if Clarity not loaded by GTM.
 * Key and value are truncated to 255 chars per Clarity limits.
 * @param {string} key - Tag name (e.g. journey_stage, journey_event)
 * @param {string} value - Tag value (e.g. plan_view, parent)
 */
export function setClarityTag(key, value) {
  if (!key || value == null) return
  if (!isClarityTagAllowed()) return
  if (!isClarityLoaded()) {
    if (!clarityTagLogged) clarityTagLogged = true
    return
  }
  const k = String(key).slice(0, MAX_TAG_LENGTH)
  const v = String(value).slice(0, MAX_TAG_LENGTH)
  try {
    window.clarity('set', k, v)
  } catch (_) {}
}

/**
 * Set multiple tags in one go (e.g. stage + event + user_type).
 * @param {Record<string, string>} tags - Object of key -> value
 */
export function setClarityTags(tags) {
  if (!tags || typeof tags !== 'object') return
  const allowed = isClarityTagAllowed()
  const loaded = isClarityLoaded()
  if (allowed && loaded && !clarityTagLogged) clarityTagLogged = true
  Object.entries(tags).forEach(([k, v]) => {
    if (v != null && v !== '') setClarityTag(k, v)
  })
}

function getCookieValue(cookieName) {
  if (typeof document === 'undefined' || !document.cookie) return null
  const cookies = document.cookie.split('; ')
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split('=')
    if (parts[0] === cookieName) {
      return decodeURIComponent(parts[1] || '')
    }
  }
  return null
}

function splitClarityCookie(value) {
  if (!value || typeof value !== 'string') return null
  if (value.includes('^')) return value.split('^')[0]
  if (value.includes('|')) return value.split('|')[0]
  return value
}

const CLARITY_PLAYER_BASE = 'https://clarity.microsoft.com/player'

function getClarityUrlFromDataLayer() {
  if (
    typeof window === 'undefined' ||
    !window.dataLayer ||
    !Array.isArray(window.dataLayer)
  )
    return null
  const dl = window.dataLayer
  for (let i = dl.length - 1; i >= 0; i--) {
    const url = dl[i]?.clarity_playback_url
    if (url && typeof url === 'string' && url.startsWith(CLARITY_PLAYER_BASE))
      return url
  }
  return null
}

/**
 * Returns the current Clarity session replay/debug URL if available.
 * 1) GTM (Option 1): dataLayer key clarity_playback_url. In GTM, add a tag that pushes
 *    {{Microsoft Clarity Playback URL}} to dataLayer as clarity_playback_url (e.g. on DOM Ready).
 * 2) clarity("metadata") callback when Clarity is loaded.
 * 3) Cookies (_clck, _clsk) + VITE_CLARITY_PROJECT_ID as fallback.
 * @returns {Promise<string|null>}
 */
export function getClaritySessionUrl() {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (isYouthEnvironment()) return Promise.resolve(null)
  const fromDataLayer = getClarityUrlFromDataLayer()
  if (fromDataLayer) return Promise.resolve(fromDataLayer)
  return new Promise((resolve) => {
    let settled = false
    const once = (value) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('metadata', (metadata) => {
          if (
            metadata &&
            metadata.projectId &&
            metadata.userId &&
            metadata.sessionId
          ) {
            const url = `${CLARITY_PLAYER_BASE}/${metadata.projectId}/${metadata.userId}/${metadata.sessionId}`
            once(url)
          } else {
            once(null)
          }
        })
        setTimeout(() => once(null), 1500)
      } else {
        const projectId =
          typeof import.meta !== 'undefined' &&
          import.meta.env?.VITE_CLARITY_PROJECT_ID
            ? import.meta.env.VITE_CLARITY_PROJECT_ID
            : null
        const clsk = getCookieValue('_clsk')
        const clck = getCookieValue('_clck')
        if (projectId && clsk && clck) {
          const clskPart = splitClarityCookie(clsk)
          const clckPart = splitClarityCookie(clck)
          if (clskPart && clckPart) {
            once(`${CLARITY_PLAYER_BASE}/${projectId}/${clckPart}/${clskPart}`)
            return
          }
        }
        once(null)
      }
    } catch (e) {
      once(null)
    }
  })
}

export default {
  setClarityTag,
  setClarityTags,
  isClarityLoaded,
  isProductionDomain,
  getClaritySessionUrl
}
